import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Plus, LayoutDashboard } from 'lucide-react';
import { currentUser, hasAdminAccess, clearToken, MIN_LEVEL, type AuthUser } from '@/lib/auth';
import { api, SiteSummary } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { ToastProvider, useToast } from '@/components/ui/Toast';
import { AddSiteDialog } from '@/components/AddSiteDialog';

export default function AdminLayout() {
  return (
    <ToastProvider>
      <AdminLayoutInner />
    </ToastProvider>
  );
}

function AdminLayoutInner() {
  const navigate = useNavigate();
  const { push } = useToast();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checked, setChecked] = useState(false);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  const refreshSites = useCallback(async () => {
    try {
      const list = await api.listSites();
      setSites(list);
    } catch (e) {
      push(e instanceof Error ? e.message : '사이트 목록 로드 실패', 'error');
    }
  }, [push]);

  useEffect(() => {
    const u = currentUser();
    if (!u) {
      navigate('/login', { replace: true });
      return;
    }
    setUser(u);
    setChecked(true);
  }, [navigate]);

  useEffect(() => {
    if (checked && user && hasAdminAccess()) void refreshSites();
  }, [checked, user, refreshSites]);

  if (!checked || !user) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">인증 확인 중…</div>;
  }

  if (!hasAdminAccess()) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
          <h1 className="mb-2 text-xl font-bold">접근 권한 부족</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            site_manager는 레벨 {MIN_LEVEL} 이상만 접근할 수 있습니다. (현재 Lv.{user.level})
          </p>
          <Button
            onClick={() => {
              clearToken();
              navigate('/login', { replace: true });
            }}
          >
            다른 계정으로 로그인
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <aside className="w-64 shrink-0 border-r bg-card">
        <div className="px-4 py-5">
          <div className="text-lg font-bold">Site Manager</div>
          <div className="text-xs text-muted-foreground">원데이샘플 CMS</div>
        </div>

        <div className="px-2">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
              )
            }
          >
            <LayoutDashboard className="h-4 w-4" />
            대시보드
          </NavLink>
        </div>

        <div className="mt-6 px-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">사이트</div>
            <button
              onClick={() => setAddOpen(true)}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="새 사이트"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="mt-1 px-2">
          {sites.length === 0 ? (
            <div className="px-3 py-6 text-xs text-muted-foreground">
              사이트가 없습니다. + 버튼으로 추가하세요.
            </div>
          ) : (
            sites.map((s) => <SiteNavItem key={s.id} site={s} />)
          )}
        </div>

        <div className="mt-auto border-t px-4 py-3 text-xs text-muted-foreground">
          <div className="truncate">{user.email}</div>
          <div>Lv.{user.level}</div>
          <button
            className="mt-2 text-destructive hover:underline"
            onClick={() => {
              clearToken();
              navigate('/login', { replace: true });
            }}
          >
            로그아웃
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden p-6">
        <Outlet context={{ sites, refreshSites }} />
      </main>

      <AddSiteDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => {
          void refreshSites();
        }}
      />
    </div>
  );
}

function SiteNavItem({ site }: { site: SiteSummary }) {
  const location = useLocation();
  const prefix = `/admin/sites/${site.slug}`;
  const isActive = location.pathname === prefix || location.pathname.startsWith(prefix + '/');
  return (
    <NavLink
      to={prefix}
      className={cn(
        'flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
        isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
      )}
    >
      <span className="truncate">{site.name}</span>
      <span
        className={cn(
          'rounded-full px-1.5 py-0.5 text-[10px]',
          isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
        )}
      >
        {site.active_item_count}
      </span>
    </NavLink>
  );
}

export interface OutletCtx {
  sites: SiteSummary[];
  refreshSites: () => Promise<void>;
}
